
import _ from "lodash";
import moment from "moment";


class NotProvided {}


/*
    Abstract constraint

    cond_cb() result: true - perform validation, false - assume validation error, null - skip validation
    result_cb() returns modified result object
 */
export class AbstractConstraint {
    constructor(msg=null, cond_cb=null, result_cb=null, ...other) {
        this.msg = (
            typeof msg == 'function' ?
            msg
            :
            this._defaultMsg.bind(this, msg)
        );
        this.cond_cb = cond_cb || function _defaultCondCb(value, root) { return true; };
        this.result_cb = result_cb || function _defaultResultCb(r) { return r; };
        this.other = other;
    }

    _defaultMsg(msg, r, info) {
        let childMsg = 'Invalid value';
        if(info && Array.isArray(info.results) && info.results.length > 0) {
            _.each(info.results, function(v) {
                if(v.msg !== null) {
                    childMsg = v.msg;
                    return false;
                }
            });
        }

        return (r === false) ?  (msg || childMsg) : null;
    }

    _createAttribs() {
        return [ this.msg, this.cond_cb, this.result_cb ].concat(this.other);
    }

    static isConstraint(c) {
        return (typeof c != 'undefined' && c !== null && c._isConstraint === true);
    }

    get _isConstraint() {
        return true;
    }

    isApplyable(value, root) {
        return this.cond_cb.call(this, value, root);
    }

    child(name, value, root) {
        return null;
    }

    makeResult(r, info) {
        return {
            r: r,
            msg: this.msg.call(this, r, info),
            skipped: info ? info.skipped : (r === null ? 1 : 0),
            invalid: info ? info.invalid : (r === false ? 1 : 0),
            total: info ? info.total : 1,
            results: info ? info.results : null
        };
    }

    validate(value, root=NotProvided) {
        let me = this;

        let cond = me.isApplyable(value, root);
        if(cond === null) return me.makeResult(null);
        if(cond === false) return me.makeResult(false);

        let out = me.validate_value(value, root);
        return me.result_cb.call(me, out);
    }

    validate_value(value, root) {
        return this.makeResult(null);
    }

    mangle(value, root) {
        let me = this;

        let cond = me.isApplyable(value, root);
        if(cond === null || cond === false) return value;

        return me.mangle_value(value, root);
    }

    mangle_value(value, root) {
        return value;
    }
}


export function prepareConstraint(schema, ...other) {
    if(typeof schema == 'undefined' || schema === null) {
        return new AbstractConstraint();
    }
    else if(AbstractConstraint.isConstraint(schema)) {
        return schema;
    }
    else if (Array.isArray(schema)) {
        return new SingleConstraintGroup(schema, ...other);
    }
    else {
        return new ObjectSchemaConstraint(schema, ...other);
    }
}


/*
    Single-field constraints
 */


export class NotEmptyConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;

        if (value === null) {
            return me.makeResult(false);
        }
        else if (typeof value == 'boolean') {
            return me.makeResult(true);
        }
        else if (typeof value == 'string') {
            if (value.trim().length > 0) return me.makeResult(true);
        }
        else if (typeof value == 'number') {
            if(!isNaN(value)) return me.makeResult(true);
        }
        else if (typeof value == 'object') {
            if (typeof value.length != 'undefined' && value.length > 0) return me.makeResult(true);
            if (Object.keys(value).length > 0) return me.makeResult(true);
        }
        return me.makeResult(false);
    }
}


export class ValueEqualConstraint extends AbstractConstraint {
    constructor(value, ...other) {
        super(...other);
        this.compareVal = value;
    }
    validate_value(value) {
        let me = this;

        return me.makeResult(value === me.compareVal);
    }
}

export class LengthRangeConstraint extends AbstractConstraint {
    constructor(min=null, max=null, ...other) {
        super(...other);
        this.min = min;
        this.max = max;
    }

    validate_value(value) {
        let me = this,
            len = undefined;

        if(Array.isArray(value) || typeof value == 'string') len = value.length;
        if(typeof value == 'object') len = Object.keys(value).length;

        if(typeof len == 'undefined' || len === null) {
            return me.makeResult(null);
        }

        if(me.min !== null) {
            if(len < me.min) return me.makeResult(false);
        }

        if(me.max !== null) {
            if(len > me.max) return me.makeResult(false);
        }

        return me.makeResult(true);
    }
}


export class ValidNumberConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;

        if(typeof value == 'undefined' || value === null) {
            return me.makeResult(null);
        }

        if(typeof value == 'string') {
            if(value.length == 0) return me.makeResult(null);
            value = Number(value)
        }

        if(typeof value == 'number') {
            return me.makeResult(!isNaN(value));
        }

        return me.makeResult(false);
    }
}


export class ValidFullNameConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;

        if(typeof value == 'undefined' || value === null) {
            return me.makeResult(null);
        }

        // TODO: analyze already splitted array and standart objects
        if(typeof value == 'object') {
            return me.makeResult(null);
        }

        if(typeof value == 'string') {
            value = _.filter(_.split(value, /\s+/g), (v) => v.length);

            if (value.length == 0) {
                return me.makeResult(null);
            }
            else if (value.length >= 2 && value.length <= 4) {
                return me.makeResult(true);
            }
        }

        return me.makeResult(false);
    }
}


export class ValidPhoneConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;

        if(typeof value == 'undefined' || value == null) {
            return me.makeResult(null);
        }

        if(typeof value == 'number') value = String(value);

        if(typeof value == 'string') {
            value = _.replace(value, /\s/g, '');
            let fvalue = _.replace(value, /\D/g, '');

            if (value.length == 0 && fvalue.length == 0) {
                return me.makeResult(null);
            }

            if (fvalue.length >= 6 && fvalue.length <= 11) {
                return me.makeResult(true);
            }
        }

        return me.makeResult(false);
    }
}


export class ValidDateConstraint extends AbstractConstraint {
    validate_value(value) {
        const me = this;

        if(_.isNil(value)) {
            return me.makeResult(null);
        }

        if(_.isString(value)) {
            if(value.length == 0) return me.makeResult(null);

            const date2 = moment(value, 'YYYY-MM-DD', true);
            if (date2.isValid()) return me.makeResult(true);

            return me.makeResult(false);
        }

        return me.makeResult(false);
    }

    mangle_value(value, root) {
        if(_.isNil(value)) return value;

        if(this.validate_value(value).r === true) {
            const date2 = moment(value, 'YYYY-MM-DD', true);
            if(date2.isValid()) return date2.format('YYYY-MM-DD');
        }
        return value;
    }
}


export class ValidTaxIdConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;
        if(typeof value == 'undefined' || value === null) return me.makeResult(null);
        if(typeof value == 'string') {
            let arr = value;
            if(!arr.length) return me.makeResult(null);
            if(arr.length === 10) {
                let out = ((arr[0] * 2) + (arr[1] * 4) + (arr[2] * 10)
                        + (arr[3] * 3) + (arr[4] * 5) + (arr[5] * 9)
                        + (arr[6] * 4) + (arr[7] * 6) + (arr[8] * 8)) % 11 % 10;
                return me.makeResult(out == arr[9]);

            } else if(arr.length === 12) {
                let out1 = ((arr[0] * 7) + (arr[1] * 2) + (arr[2] * 4) + (arr[3] * 10)
                         + (arr[4] * 3) + (arr[5] * 5) + (arr[6] * 9) + (arr[7] * 4)
                         + (arr[8] * 6) + (arr[9] * 8)) % 11 % 10;
                let out2 = ((arr[0] * 3) + (arr[1] * 7) + (arr[2] * 2) + (arr[3] * 4)
                         + (arr[4] * 10) + (arr[5] * 3) + (arr[6] * 5) + (arr[7] * 9)
                         + (arr[8] * 4) + (arr[9] * 6) + (arr[10] * 8)) % 11 % 10;
                return me.makeResult(out1 == arr[10] && out2 == arr[11]);

            }
            return me.makeResult(false);
        }
        return me.makeResult(false);
    }
    mangle_value(value, root) {
        if(typeof value == 'undefined' || value === null) return value;
        if(typeof value != 'string') return '';
        return value.replace(/\D/, '');
    }
}


export class ValidSsnConstraint extends AbstractConstraint {
    validate_value(value) {
        let me = this;

        if(_.isNil(value)) {
            return me.makeResult(null);
        }

        if(_.isString(value)) {
            if(!value.length) return me.makeResult(null);

            let arr = value.replace(/\D/g, '');
            if(arr.length !== 11) return me.makeResult(false);

            let sum = 0;
            for(let i = 0; i < 9; i++)  {
                sum += parseInt(value[i]) * (9 - i);                
            }

            let checkDigit = 0;
            if(sum < 100) {
                checkDigit = sum;

            } else if(sum > 101) {
                checkDigit = parseInt(sum % 101);
                if(checkDigit == 100) {
                    checkDigit = 0;
                }
            }

            return me.makeResult(checkDigit == parseInt(value.slice(-2)));
        }
        
        return me.makeResult(false);
    }
    mangle_value(value, root) {
        if(typeof value == 'undefined' || value === null) return value;
        if(typeof value != 'string') return '';
        return value.replace(/\D/, '');
    }
}


/*
    Validate immediate value by defined schema

    @param constraints: array of constraints
 */
export class SingleConstraintGroup extends AbstractConstraint {
    constructor(constraints, ...others) {
        super(...others);
        if(!Array.isArray(constraints)) constraints = [ constraints ];
        this.constraints = _.map(constraints, function(v) { return prepareConstraint(v, ...others)});
    }

    child(name, value, root) {
        let me = this,
            out = [];

        if(me.isApplyable(value, root) !== true) return null;

        _.each(this.constraints, function(c) {
            let co = c.child(name, value, root);
            if(co !== null) out.push(co);
        });

        return (out.length) == 1 ? out[0] : new SingleConstraintGroup(out, ...me._createAttribs());
    }

    validate_value(value, root) {
        let me = this;

        let outResults = [],
            outSkipped = 0,
            outInvalid = 0,
            outTotal = 0,
            outResult = true,
            outMsg = null;

        // validate each value by each constraint
        _.each(me.constraints, function(c) {
            let r = c.validate(value, root);

            if(r.r !== null) outResult = outResult && r.r;
            if(r.r === null) outSkipped ++;
            if(r.r === false) outInvalid ++;

            outResults.push(r);
            outTotal ++;
        });
        if(outSkipped == outTotal) outResult = null;

        return me.makeResult(outResult, {
            skipped: outSkipped,
            invalid: outInvalid,
            total: outTotal,
            results: outResults
        });
    }

    mangle_value(value, root) {
        let me = this,
            out = value;

        _.each(me.constraints, function(c) {
            out = c.mangle(out, root);
        });

        return out;
    }
}


/*
    Validate array of values by defined schema
    @param constraints: array of constraints
 */
export class ArrayConstraintGroup extends AbstractConstraint {
    constructor(constraints, ...others) {
        super(...others);
        if(!Array.isArray(constraints)) constraints = [ constraints ];
        this.constraints = _.map(constraints, function(v) { return prepareConstraint(v, ...others)});
    }

    child(name, value, root) {
        let me = this,
            out = [];

        if(me.isApplyable(value, root) !== true) return null;

        _.each(this.constraints, function(c) {
            let co = (name !== null) ? c.child(name, value, root) : c;
            if(co !== null) out.push(co);
        });

        return (out.length) == 1 ? out[0] : new SingleConstraintGroup(out, ...me._createAttribs());
    }

    validate_value(value, root) {
        let me = this;

        if(typeof value == 'undefined' || value === null) return me.makeResult(null, {
            skipped: 1,
            invalid: 0,
            total: 1,
            results: []
        });
        if(!Array.isArray(value)) return me.makeResult(false, {
            skipped: 0,
            invalid: 1,
            total: 1,
            results: []
        });

        let outResults = [],
            outSkipped = 0,
            outInvalid = 0,
            outTotal = 0,
            outResult = true;

        let c = prepareConstraint(me.constraints);

        _.each(value, function(v) {
            let r = c.validate(v, root);

            if(r.r !== null) outResult = outResult && r.r;
            if(r.r === null) outSkipped ++;
            if(r.r === false) outInvalid ++;

            outResults.push(r);
            outTotal ++;
        });
        if(outSkipped == outTotal) outResult = null;

        return me.makeResult(outResult, {
            skipped: outSkipped,
            invalid: outInvalid,
            total: outTotal,
            results: outResults
        });
    }
}


/*
    Validate schema for single object

    @param schema: object where keys named corresponding to data field keys
        values can contain array of constraints or single constraint object
 */
export class ObjectSchemaConstraint extends AbstractConstraint {
    constructor(schema, ...others) {
        super(...others);
        this.schema = schema || {};
    }

    child(name, value, root) {
        return new SingleConstraintGroup(this.schema[name], ...this._createAttribs());
    }

    validate_value(value, root) {
        let me = this;

        if(typeof value == 'undefined' || value === null || typeof value != 'object') return me.makeResult(false);

        let outResults = {},
            outSkipped = 0,
            outInvalid = 0,
            outTotal = 0,
            outResult = true;

        _.each(me.schema, function (c_list, field) {
            let c = prepareConstraint(c_list),
                r = c.validate(value[field], root);

            outResults[field] = r.results;
            if (r.r !== null) outResult = outResult && r.r;
            if (r.skipped == r.total) outSkipped++;
            if (r.invalid > 0) outInvalid++;
            outTotal++;
        });

        if (outSkipped == outTotal) outResult = null;

        return me.makeResult(outResult, {
            skipped: outSkipped,
            invalid: outInvalid,
            total: outTotal,
            results: outResults
        });
    }

    mangle_value(value, root) {
        let me = this,
            out = {};

        _.each(me.schema, function(c_list, field) {
            out[field] = prepareConstraint(c_list).mangle(value[field], root);
        });

        return out;
    }
}


/*
    Data-driven constaints
 */


export class EqualsToConstraint extends AbstractConstraint {
    constructor(to, ...others) {
        super(...others);
        this.to = to;
    }

    validate_value(value, root) {
        let me = this,
            value2 = root[me.to];

            if(typeof value == 'undefined' || typeof value2 == 'undefined') return me.makeResult(null);
            return me.makeResult(value == value2);
    }
}


/*
    Mangle convenience classes
 */


export class DefaultValue extends AbstractConstraint {
    constructor(def, ...others) {
        super(...others);
        
        const defX = def;
        this.def = _.isFunction(defX) ? defX : () => defX;
    }

    mangle_value(value, root) {
        if(typeof value == 'undefined' || value === null) value = this.def();
        return value;
    }
}


export class ExplicitValue extends AbstractConstraint {
    constructor(def, ...others) {
        super(...others);
        this.def = def;
    }

    mangle_value(value, root) {
        return this.def;
    }
}


export class CopyValue extends AbstractConstraint {
    constructor(src, ...others) {
        super(...others);
        this.src = src;
    }

    mangle_value(value, root) {
        return root[this.src];
    }
}


export class ComputeValue extends AbstractConstraint {
    constructor(cb, ...others) {
        super(...others);
        this.compute_cb = cb || _.identity();
    }

    mangle_value(value, root) {
        return this.compute_cb.call(this, value, root);
    }
}


/*
    Shortcut functions
 */

export function validateOnly(data, schema, fields, root=NotProvided, ...other) {
    root = (root === NotProvided) ? data : root;

    return prepareConstraint(
        _.fromPairs(
            _.map(fields || [], function(x) {
                return [x, schema.child(x, _.get(x, data), root)];
            })
        )
    )
}


/*
    Validate schema:
    @param schema: object of {fieldname: [validator, validator...]}
    @param data: object of data {fieldname: value}
    @param other: custom parameters passed to all validators
 */
export function validateSchema(data, schema, root=NotProvided, ...other) {
    root = (root === NotProvided) ? data : root;

    let c = prepareConstraint(schema, ...other);
    return c.validate(data, (root === NotProvided) ? data : root);
}


/*
    Modify data by schema:
    @param schema: object of {fieldname: [validator, validator...]}
    @param data: object of data {fieldname: value}
    @param other: custom parameters passed to all validators
 */
export function mangleDataBySchema(data, schema, root=NotProvided, ...other) {
    root = (root === NotProvided) ? data : root;

    let c = prepareConstraint(schema, ...other);
    return c.mangle(data, root);
}
